import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getLastCheckin, recordCheckin } from "@/features/attendance/attendance.api";
import type { Checkin, LastCheckin } from "@/features/attendance/schemas/attendance.schema";

/// The caller's last log and what the next tap will record.
///
/// The direction comes from the server rather than being derived here, so a
/// stale tab cannot show "Check out" while the server would record an arrival.
export function useLastCheckin(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ["attendance", "last-checkin", workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId required");
      return getLastCheckin(workspaceId);
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    // A `staleTime` only says when data MAY be refetched, never that anything
    // will ask (the same trap `presence-boundary.tsx` documents). The client
    // default is `refetchOnWindowFocus: false`, so a tap made on a phone left
    // this tab insisting "Not checked in" until something else happened to
    // re-render it. Focus is the moment somebody comes back to the desk.
    refetchOnWindowFocus: true,
  });
}

/// Record a tap.
///
/// reason: pessimistic, with no optimistic write. The server decides the
/// direction, resolves which shift the tap belongs to, and may reject it as a
/// duplicate. Showing a confirmed arrival that the server then refused would
/// be worse than a moment's latency on a twice-daily action.
export function useRecordCheckin(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Checkin,
    Error,
    { logType?: Checkin["logType"]; time?: string; localTime?: string } | void
  >({
    mutationFn: async (input) => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return recordCheckin(workspaceId, input ?? undefined);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["attendance", "last-checkin", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["attendance", "checkins", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["attendance", "mine", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["attendance", "day", workspaceId] });
    },
  });
}

/// The label for the next action, from the server's own answer.
export function nextActionLabel(last: LastCheckin | undefined): string {
  return last?.nextLogType === "check_out" ? "Check out" : "Check in";
}
